import { computed, Injectable, signal, Signal } from "@angular/core";
import { Area, ImageType, Assertion, AssertionIssue, Component, db, InspectionJob, JobIssue, JobIssueVendor, JobSignature, Lot, Media, Zone, SyncableStatus } from "../../../../../core/database/db";
import { MediaService } from "../../../../../domains/media/services/media.service";
import { JobIssueService } from "../../../../../domains/jobIssues/services/job-issue-service";
import { BqStepDataService } from "../../../../shared/bq-step-data-service/bq-step-data-service-factory";
import { AreaConfigurationDTO } from "../../../../../domains/areas/models/configuration/AreaConfigurationDTO";
import { JobIssueVendorService } from "../../../../../domains/jobIssues/services/job-issue-vendor.service";
import { VendorDTO } from "../../../../../domains/vendors/models/VendorDTO";
import { VendorDataHelperService } from "../../../../../domains/vendors/services/vendor-data-helper.service";
import { JobSignatureService } from "../../../../../domains/jobSignatures/services/job-signature-service";
import { LoggerService } from "../../../../../shared/services/logger.service";

@Injectable()
export class Bq9DataService implements BqStepDataService {

  constructor(
    private mediaService: MediaService,
    private jobIssueService: JobIssueService,
    private jobIssueVendorService: JobIssueVendorService,
    private jobSignatureService: JobSignatureService,
    private logger: LoggerService,
    private vendorDataHelperService: VendorDataHelperService
  ) { }

  readonly job = signal<InspectionJob | undefined>(undefined);
  readonly lot = signal<Lot | undefined>(undefined);
  readonly areas = signal<Area[] | undefined>(undefined);
  readonly jobIssues = signal<JobIssue[] | undefined>(undefined);
  readonly media = signal<Media[] | undefined>(undefined);
  readonly zones = signal<Zone[] | undefined>(undefined);
  readonly areasConfig = signal<AreaConfigurationDTO[] | undefined>(undefined);
  readonly components = signal<Component[] | undefined>(undefined);
  readonly assertions = signal<Assertion[] | undefined>(undefined);
  readonly assertionIssues = signal<AssertionIssue[] | undefined>(undefined);
  readonly isCcmInspection = signal<boolean>(false);

  readonly jobIssueVendors = signal<JobIssueVendor[] | undefined>(undefined);
  readonly vendors = signal<VendorDTO[]>([]);
  readonly fromLotSummary = signal<boolean>(false);
  readonly issuesSavingMap = signal<Record<string, boolean>>({});
  readonly bq8HomeownerEmails = signal<string[]>([]);

  isRefreshingInBackground: Signal<boolean> = signal(false);
  dataUpdatedPulse: Signal<boolean> = signal(false);

  readonly hasData = computed(() => {
    return !!this.job() && !!this.lot() && !!this.areas() && !!this.jobIssues();
  });

  async updateCompleteIssueMedia(issue: JobIssue, mediaToSave: Media[]): Promise<void> {
    const rekeyedMedia = mediaToSave.map(m => ({ ...m, parentIdentifier: issue.identifier }));

    await db.transaction("rw", [db.media, db.syncStatus], async () => {
      await this.mediaService.softDeleteMediaForParent(issue.identifier, ImageType.ConfirmationHomeowner);
      await this.mediaService.bulkAddMedia(rekeyedMedia);
    });

    this.media.update(existing => {
      const withoutOld = (existing ?? []).filter(m =>
        !(m.parentIdentifier === issue.identifier && m.imageType === ImageType.ConfirmationHomeowner)
      );
      return [...withoutOld, ...rekeyedMedia];
    });
  }

  async saveCompleteIssue(issue: JobIssue, newImages: Media[]): Promise<void> {
    this.issuesSavingMap.update(state => ({ ...state, [issue.identifier]: true }));

    try {
      await db.transaction("rw", [db.media, db.jobIssue, db.syncStatus], async () => {
        await this.mediaService.bulkAddMedia(newImages);
        await this.jobIssueService.confirmIssueWithHomeowner(issue);
      });

      this.media.update(existingImages => {
        return [...existingImages ?? [], ...newImages];
      });

      this.jobIssues.update(existingIssues => {
        if (!existingIssues) {
          return existingIssues;
        }
        return existingIssues.map(i => (i.identifier === issue.identifier ? issue : i));
      });

    } catch (error) {
      this.logger.error("Error saving complete issue:", error);
      throw error;

    } finally {
      this.issuesSavingMap.update(state => ({ ...state, [issue.identifier]: false }));
    }
  }

  async saveNotCompleteIssue(
    bq9Issue: JobIssue,
    mediaToSave: Media[],
    selectedVendor: VendorDTO | null = null
  ): Promise<JobIssue> {
    const bq9Job = this.job();
    if (!bq9Job?.id) {
      throw new Error("Cannot create a BQ9 issue without a BQ9 job.");
    }

    const issueToSave: Omit<JobIssue, "id"> = {
      ...bq9Issue,
      jobId: bq9Job.id,
      isComplete: false,
      isConfirmedByHomeowner: false
    };

    return db.transaction("rw", [db.media, db.jobIssue, db.jobIssueVendor, db.syncStatus], async () => {
      await this.mediaService.bulkAddMedia(mediaToSave);
      const newId = await this.jobIssueService.addJobIssue(issueToSave);
      const savedIssue: JobIssue = { ...issueToSave, id: newId };

      this.media.update(existing => [...(existing ?? []), ...mediaToSave]);
      this.jobIssues.update(existingIssues => {
        if (!existingIssues) {
          return existingIssues;
        }
        const withoutSource = bq9Issue.parentIdentifier
          ? existingIssues.filter(i => i.identifier !== bq9Issue.parentIdentifier)
          : existingIssues;
        return [...withoutSource, savedIssue];
      });

      await this.addOrReplaceVendorAssignmentForIssue(savedIssue, selectedVendor, null);

      return this.jobIssues()?.find(i => i.identifier === savedIssue.identifier) ?? savedIssue;
    });
  }

  async updateNotCompleteIssue(
    existingIssue: JobIssue,
    updatedFields: Pick<JobIssue, "text" | "postCompleteStatus" | "lotAreaIdentifier">,
    mediaToSave: Media[],
    selectedVendor: VendorDTO | null
  ): Promise<void> {
    const rekeyedMedia = mediaToSave.map(m => ({ ...m, parentIdentifier: existingIssue.identifier }));
    const previousVendorAssignment = this.getVendorAssignmentForIssue(existingIssue);

    await db.transaction("rw", [db.media, db.jobIssue, db.jobIssueVendor, db.syncStatus], async () => {
      await this.mediaService.softDeleteMediaForParent(existingIssue.identifier, ImageType.Issue);
      await this.mediaService.bulkAddMedia(rekeyedMedia);
      await this.jobIssueService.updateJobIssue(existingIssue.id!, updatedFields);

      const updatedIssue: JobIssue = {
        ...existingIssue,
        ...updatedFields,
        syncStatus: SyncableStatus.Pending,
        updatedAt: new Date()
      };

      this.jobIssues.update(existing => {
        if (!existing) {
          return existing;
        }
        return existing.map(i => {
          return i.identifier === existingIssue.identifier ? updatedIssue : i;
        });
      });
      this.media.update(existing => {
        const withoutOld = (existing ?? []).filter(m =>
          !(m.parentIdentifier === existingIssue.identifier && m.imageType === ImageType.Issue)
        );
        return [...withoutOld, ...rekeyedMedia];
      });

      await this.addOrReplaceVendorAssignmentForIssue(updatedIssue, selectedVendor, previousVendorAssignment);
    });
  }
  async saveSignatures(signatures: JobSignature[], media?: Media): Promise<void> {
    try {
      const promises: Promise<void | number[]>[] = [
        this.jobSignatureService.bulkAddSignatures(signatures)
      ];

      if (media) {
        promises.push(this.mediaService.bulkAddMedia([media]));
      }

      await Promise.all(promises);
    } catch (error) {
      this.logger.error("Error saving signatures:", error);
      throw error;
    }
  }

  /** The assignment the BQ9 vendor select shows for a job issue (self-assignment, trade partner, or none). */
  getVendorAssignmentForIssue(issue: JobIssue): JobIssueVendor | null {
    return this.vendorDataHelperService.getVendorAssignmentForIssue(issue, this.jobIssueVendors());
  }

  /** Assigns a job issue to a trade partner or to the CM ("Assign to Me"), replacing the previous assignment. */
  addOrReplaceVendorAssignmentForIssue(
    issue: JobIssue,
    selectedVendor: VendorDTO | null,
    previousSelectedVendorAssignmentToReplace: JobIssueVendor | null
  ): Promise<JobIssueVendor | null> {
    return this.vendorDataHelperService.addOrReplaceVendorAssignmentForIssue(
      issue,
      selectedVendor,
      previousSelectedVendorAssignmentToReplace,
      this.jobIssueVendors,
      this.jobIssues
    );
  }

  async reopenCompleteIssue(issue: JobIssue): Promise<void> {
    if (!issue.id) {
      throw new Error("Cannot reopen issue without an id.");
    }

    await db.transaction("rw", [db.media, db.jobIssue, db.syncStatus], async () => {
      await this.jobIssueService.resetIssueConfirmation(issue.id!);
      await this.mediaService.softDeleteMediaForParent(issue.identifier, ImageType.ConfirmationHomeowner);
    });

    this.jobIssues.update(existingIssues =>
      existingIssues?.map(i =>
        (i.identifier === issue.identifier
          ? { ...i, isConfirmedByHomeowner: false, confirmedByHomeownerAt: undefined }
          : i)
      )
    );

    this.media.update(existingMedia =>
      existingMedia?.filter(m =>
        !(m.parentIdentifier === issue.identifier && m.imageType === ImageType.ConfirmationHomeowner)
      )
    );
  }

  async resetNotCompleteIssue(issue: JobIssue): Promise<void> {
    if (!issue.id) {
      throw new Error("Cannot reset issue without an id.");
    }
    if (!issue.parentIdentifier) {
      throw new Error("Cannot reset issue without a parent identifier.");
    }

    const parentIssue = await db.transaction("rw", [db.media, db.jobIssue, db.jobIssueVendor, db.syncStatus], async () => {
      await this.jobIssueService.softDeleteJobIssue(issue.id!);
      await this.mediaService.softDeleteMediaForParent(issue.identifier);
      await this.jobIssueVendorService.persistVendorForIssue(issue.identifier, null);

      const parent = await this.jobIssueService.getJobIssueByIdentifier(issue.parentIdentifier!);

      if (parent?.id) {
        await this.jobIssueService.resetIssueConfirmation(parent.id);
      }

      return parent;
    });

    this.jobIssues.update(existingIssues => {
      if (!existingIssues) {
        return existingIssues;
      }
      const withoutBq9 = existingIssues.filter(i => i.identifier !== issue.identifier);
      return parentIssue
        ? [...withoutBq9, { ...parentIssue, isConfirmedByHomeowner: false, confirmedByHomeownerAt: undefined }]
        : withoutBq9;
    });

    this.media.update(existingMedia =>
      existingMedia?.filter(m => m.parentIdentifier !== issue.identifier)
    );

    this.jobIssueVendors.update(existingVendors =>
      existingVendors?.filter(v => v.issueIdentifier !== issue.identifier)
    );
  }

  async deleteAddedItem(issue: JobIssue): Promise<void> {
    if (!issue.id) {
      throw new Error("Cannot delete issue without an id.");
    }

    await db.transaction("rw", [db.media, db.jobIssue, db.jobIssueVendor, db.syncStatus], async () => {
      await this.jobIssueService.softDeleteJobIssue(issue.id!);
      await this.mediaService.softDeleteMediaForParent(issue.identifier);
      await this.jobIssueVendorService.persistVendorForIssue(issue.identifier, null);
    });

    this.jobIssues.update(existingIssues =>
      existingIssues?.filter(i => i.identifier !== issue.identifier)
    );

    this.media.update(existingMedia =>
      existingMedia?.filter(m => m.parentIdentifier !== issue.identifier)
    );

    this.jobIssueVendors.update(existingVendors =>
      existingVendors?.filter(v => v.issueIdentifier !== issue.identifier)
    );
  }
}